import { useMemo } from "react"
import fp from "lodash/fp"
import { useDrawingArea, useXScale, useYScale } from "@mui/x-charts"

import type { SteppedReferenceLine } from "types/viewProperties.types"
import { buildSeasonalSteps } from "helpers/utils/seasonalAmpacity"

// Hide per-segment labels for segments narrower than this (px) to avoid clutter.
const MIN_LABEL_SEGMENT_WIDTH = 32

interface MuiSteppedReferenceLineProps {
  steppedLine: SteppedReferenceLine
}

/**
 * Renders a stepped horizontal reference line whose value changes over the
 * (time) x-axis, driven by a dictionary. Must be rendered as
 * a direct child of a MUI x-charts LineChart so the scale hooks resolve.
 */
const MuiSteppedReferenceLine = ({ steppedLine }: MuiSteppedReferenceLineProps) => {
  const xScale = useXScale()
  const yScale = useYScale()
  const { left, width } = useDrawingArea()

  const segments = useMemo(() => {
    if (!xScale || !yScale) return []

    const [rawMin, rawMax] = xScale.domain() as [Date | number, Date | number]
    const xMin = rawMin instanceof Date ? rawMin : new Date(rawMin)
    const xMax = rawMax instanceof Date ? rawMax : new Date(rawMax)

    return buildSeasonalSteps(steppedLine.seasonal, xMin, xMax)
  }, [xScale, yScale, steppedLine.seasonal])

  const { path, labels } = useMemo(() => {
    if (!xScale || !yScale || segments.length === 0) {
      return { path: "", labels: [] as Array<{ x: number; y: number; text: string }> }
    }

    const px = (millis: number) => {
      const scaled = xScale(new Date(millis)) as number | undefined
      if (scaled == null) return undefined
      return fp.clamp(left, left + width, scaled)
    }

    const commands: string[] = []
    const labelList: Array<{ x: number; y: number; text: string }> = []
    let prevY: number | undefined

    segments.forEach((segment, index) => {
      const startX = px(segment.startMillis)
      const endX = px(segment.endMillis)
      const y = yScale(segment.value) as number | undefined
      if (startX == null || endX == null || y == null) return

      if (prevY == null) {
        commands.push(`M ${startX} ${y}`)
      } else {
        // Vertical jump from the previous segment's value at the shared x.
        commands.push(`L ${startX} ${prevY}`)
        commands.push(`L ${startX} ${y}`)
      }
      commands.push(`L ${endX} ${y}`)
      prevY = y

      // Always label the first segment (with the legend prefix); label the rest
      // only when wide enough to avoid clutter. Labels are anchored at the start.
      const isFirst = index === 0
      if (isFirst || endX - startX >= MIN_LABEL_SEGMENT_WIDTH) {
        const valueText =
          steppedLine.valueLabels?.[segment.value] ?? String(segment.value)
        const text =
          isFirst && steppedLine.label ? `${steppedLine.label} ${valueText}` : valueText
        labelList.push({ x: startX + 3, y: y - 4, text })
      }
    })

    return { path: commands.join(" "), labels: labelList }
  }, [xScale, yScale, segments, left, width, steppedLine.valueLabels, steppedLine.label])

  if (!path) return null

  const lineStyle: React.CSSProperties = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 0.75,
    ...steppedLine.lineStyle,
  }
  const labelStyle = (steppedLine.labelStyle ?? {}) as React.CSSProperties

  return (
    <>
      <path d={path} pointerEvents="none" style={lineStyle} />
      {labels.map((label, index) => (
        <text
          key={`${label.x}-${index}`}
          x={label.x}
          y={label.y}
          textAnchor="start"
          pointerEvents="none"
          style={labelStyle}
        >
          {label.text}
        </text>
      ))}
    </>
  )
}

export default MuiSteppedReferenceLine
